import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import { STATUS_CODES } from '@/services/api/base'
import api from '@/my/modules/account/api/customers.api'
import apiCustomer from '@/my/modules/account/api/customers.api'
import { reportError } from '@/services/error-reporting'
import { getError } from '@/utils/errors'
import paymentCardsApi from '@/my/modules/payment-methods/api/payment-cards.api'

export const useCustomerStore = defineStore('customer', () => {
  const id = ref('')
  const email = ref('')
  const name = ref('')
  const phone = ref('')
  const pendingEmail = ref('')
  const mfaRequired = ref(false)
  const loaded = ref(false)
  const authStore = useAuthStore()
  const paymentCards = ref<PaymentCard[]>([])

  const primaryCard = computed<PaymentCard | undefined>(() => {
    return paymentCards.value.find((card) => card.primary)
  })

  async function loadFromSessionToken() {
    if (loaded.value) return

    if (authStore.isAuthenticated) {
      const response = await api.getCustomerDetails()

      if (!response.error) {
        id.value = response.id
        email.value = response.email
        name.value = response.name
        phone.value = response.phone || ''
        pendingEmail.value = response.pending_email || ''
        mfaRequired.value = response.mfa_required || false
        loaded.value = true
      } else {
        if (response.status_code == STATUS_CODES.UNAUTHORIZED) {
          authStore.clear()
          loaded.value = false
        } else {
          reportError('Error fetching customer details', {
            context: { response },
            tags: 'customer'
          })
        }
      }
    }
  }

  async function getPrimaryCardDetails(): Promise<PaymentCard | undefined> {
    const result = await apiCustomer.getPrimaryCardDetails()
    if (result && !result.error) return mapResponseToPaymentCard(result)
  }

  function mapResponseToPaymentCard(response: PaymentCardResponse): PaymentCard {
    return {
      id: response.id,
      last4: response.last_four,
      expMonth: response.exp_month,
      expYear: response.exp_year,
      network: response.network,
      funding: response.funding,
      primary: response.primary,
      insertedAt: response.inserted_at,
      status: response.status,
      expDate: response.exp_date,
      expired: response.expired,
      expiresSoon: response.expires_soon
    }
  }

  async function loadPaymentCards() {
    const response = await paymentCardsApi.getPaymentCards(null)
    if (response && !response.error) {
      paymentCards.value = response.data.map(mapResponseToPaymentCard)
    }
  }

  async function requestEmailChange(newEmail: string): Promise<ApiNoContentResponse> {
    return await apiCustomer.requestEmailChange(newEmail)
  }

  async function resendChangeEmailInstructions(): Promise<ApiNoContentResponse> {
    return await apiCustomer.resendChangeEmailInstructions()
  }

  async function updateDetails(details: CustomerDetailsRequest): Promise<ApiNoContentResponse> {
    const result = await apiCustomer.updateDetails(details)

    if (result.error) {
      return { error: getError(result) }
    }

    email.value = details['email'] ?? email.value
    name.value = details['name'] ?? name.value
    phone.value = details['phone'] ?? phone.value
    pendingEmail.value = details['pending_email'] ?? pendingEmail.value
    mfaRequired.value = details['mfa_required'] ?? mfaRequired.value

    return {}
  }

  async function verifyEmailChange(token: string): Promise<VerifyEmailChangeResponse> {
    return await apiCustomer.verifyEmailChange(token)
  }

  function clear() {
    id.value = ''
    email.value = ''
    name.value = ''
    phone.value = ''
    pendingEmail.value = ''
    loaded.value = false
    mfaRequired.value = false
    paymentCards.value = []
  }

  return {
    id,
    email,
    name,
    phone,
    clear,
    loaded,
    pendingEmail,
    mfaRequired,
    loadFromSessionToken,
    getPrimaryCardDetails,
    requestEmailChange,
    resendChangeEmailInstructions,
    updateDetails,
    verifyEmailChange,
    primaryCard,
    paymentCards,
    loadPaymentCards
  }
})
